import { useEffect, useRef, useState, useCallback } from 'react'
import styled from '@emotion/styled'
import { VideoSeekSlider } from 'react-video-seek-slider'
import { PiPlayFill, PiPauseFill } from 'react-icons/pi'
import ReactHlsPlayer from 'react-hls-player'
import 'react-video-seek-slider/styles.css'
import '@styles/hls-player.css'

const PlayerContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  height: 100vh;
  width: 100vw;
  background-color: black;
`

const VideoOverlay = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
`

const ControlsArea = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  bottom: 0;
  left: 0;
  width: 100%;
  padding: 1rem;
`

const PlayButton = styled.button`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding: 0.5rem 1rem 0.5rem 0;
  
  background: transparent;
  color: white;
  border: none;
`

const SliderContainer = styled.div`
  flex-grow: 1;
`

const VideoControls = ({ player }) => {
  const [reRender, setRerender] = useState(true)
  const forceRender = () => setRerender(!reRender)
  const interval = useRef(null)
  const [currentTime, setCurrentTime] = useState(0)
  const [progress, setProgress] = useState(0)
  const [maxTime, setMaxTime] = useState(0)
  // console.log(player)

  // NOTE: Putting these re-rendering callc in the other callbacks results in
  //       extreme delays for some reason... but we do NEED to rerender the actual
  //       player state because the local state can get out of sync.
  // 
  // Causes the component to render the correct player state when pause happens
  useEffect(() => {
    const callback = (e) => forceRender()
    player?.current?.addEventListener('pause', callback)
    return () => player?.current?.removeEventListener('pause', callback)
  }, [player, forceRender])

  // Causes the component to render the correct player state when play happens
  useEffect(() => {
    const callback = (e) => forceRender()
    player?.current?.addEventListener('play', callback)
    return () => player?.current?.removeEventListener('play', callback)
  }, [player, forceRender])

  function playVideo() {
    player.current.play()
  }
  
  function pauseVideo() {
    player.current.pause()
  }

  const handleTimeChange = useCallback((time, offsetTime) => {
    if (!player.current?.currentTime) {
      return;
    }

    player.current.currentTime = time / 1000;
    setCurrentTime(time);

    console.log({ time, offsetTime });
  }, [player]);

  const handlePlay = () => {
    interval.current = setInterval(() => {
      setCurrentTime((player.current?.currentTime || 0) * 1000);
    }, 500);
  };

  const handlePause = () => {
    clearInterval(interval.current);
    console.log('pausing play interval')
  };

  const handleDataLoaded = () => {
    setMaxTime((player?.current?.duration || 0) * 1000);
    console.log('set max time')
  };

  const handleProgress = () => {
    const buffer = player?.current?.buffered;

    if (((buffer?.length > 0 && player.current?.duration) || 0) > 0) {
      let currentBuffer = 0;
      const inSeconds = player.current?.currentTime || 0;

      for (let i = 0; i < buffer.length; i++) {
        if (buffer.start(i) <= inSeconds && inSeconds <= buffer.end(i)) {
          currentBuffer = i;
          break;
        }
      }
      console.log('setting progress buffer')
      console.log(buffer.end(currentBuffer) * 1000 || 0)
      setProgress(buffer.end(currentBuffer) * 1000 || 0)
    }
  };

  useEffect(() => {
    if (!player) {
      return;
    }

    player.current?.addEventListener("play", handlePlay);
    player.current?.addEventListener("pause", handlePause);
    player.current?.addEventListener("loadeddata", handleDataLoaded);
    player.current?.addEventListener("progress", handleProgress);
    return () => {
      player.current?.removeEventListener("play", handlePlay);
      player.current?.removeEventListener("pause", handlePause);
      player.current?.removeEventListener("loadeddata", handleDataLoaded);
      player.current?.removeEventListener("progress", handleProgress);
    }
  }, [player]);

  return (
    <VideoOverlay>
      <ControlsArea>
        <PlayButton onClick={player.current.paused ? playVideo : pauseVideo}>
          {player.current.paused 
            ? <PiPlayFill size='1.25rem' color='white' style={{ flexShrink: 0 }} />
            : <PiPauseFill size='1.25rem' color='white' style={{ flexShrink: 0 }} />
          }
        </PlayButton>
        <SliderContainer>
          <VideoSeekSlider
            max={maxTime}
            currentTime={currentTime}
            bufferTime={progress}
            onChange={handleTimeChange}
            limitTimeTooltipBySides={true}
            secondsPrefix="00:"
            minutesPrefix="0:"
          />
        </SliderContainer>
      </ControlsArea>
    </VideoOverlay>
  )
}

const HlsPlayer = ({ video, autoPlay=false, containerStyles }) => {
  const playerRef = useRef()
  const [player, setPlayer] = useState(null)

  useEffect(() => {
    setPlayer(playerRef)
  }, [])

  return (
    <PlayerContainer style={containerStyles}>
      <ReactHlsPlayer
        playerRef={playerRef}
        src={video?.url}
        // src="https://vz-b38ef6d2-88e.b-cdn.net/e1a2b452-ae9e-4ee1-896c-a9cfa59fb2e5/playlist.m3u8"
        // src="https://vz-b38ef6d2-88e.b-cdn.net/5416fb81-5a89-47cb-929e-75a20a71c112/playlist.m3u8"
        // src="https://vz-b38ef6d2-88e.b-cdn.net/4cbe09d7-d560-49e5-a63b-a137696321ef/playlist.m3u8"
        autoPlay={autoPlay}
        controls={false}
        playsInline
        width="100%"
        height="100%"
      />
      {player?.current && <VideoControls player={player} />}
    </PlayerContainer>
  )
}

export default HlsPlayer
